/*
 * DYNAMIC FORM
 *
 * Renders schema fields with validation and submit/delete actions.
 * Recent changes:
 * - Supports optional labelled field groups and display-label overrides without changing form keys.
 * - Adds default-preserving field text and row-density options for embedded forms.
 * - Shows remaining characters and safely truncates oversized paste for limited textareas.
 * - Preserves legacy over-limit textarea values while blocking growth and submission.
 */

import { ChangeEvent, ClipboardEvent, FC, useRef, useState } from 'react';
import { FormField, FormConstraint } from './FormField';
import { FieldOptions as OPTIONS } from '../../models/Schema';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { useNotification } from '../../context/NotificationContext';

interface DynamicFormProps {
  fields: ReadonlyArray<FormField>;
  initialData?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => Promise<void>;
  onCancel?: (wasUpdated: boolean) => void;
  onDelete?: () => Promise<void>;
  submitLabel?: string;
  cancelLabel?: string;
  disabled?: boolean;
  canDelete?: boolean;
  idPrefix?: string;
  constrainFieldGrid?: boolean;
  fieldTextSize?: 'xs' | 'sm';
  compactRowSpacing?: boolean;
  fieldGroups?: ReadonlyArray<DynamicFormFieldGroup>;
  fieldLabelOverrides?: Readonly<Record<string, string>>;
  ungroupedFieldGroupLabel?: string;
}

export interface DynamicFormFieldGroup {
  label: string;
  fieldNames: ReadonlyArray<string>;
}

const DynamicForm: FC<DynamicFormProps> = ({
  fields,
  initialData = {},
  onSubmit,
  onCancel,
  onDelete,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  disabled = false,
  canDelete = false,
  idPrefix = 'dynamic-form',
  constrainFieldGrid = true,
  fieldTextSize = 'xs',
  compactRowSpacing = false,
  fieldGroups,
  fieldLabelOverrides,
  ungroupedFieldGroupLabel = 'Additional details'
}) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [fieldCharacterCounts, setFieldCharacterCounts] = useState<Record<string, number>>({});
  const textareaValues = useRef<Record<string, string>>({});
  const { showNotification } = useNotification();

  const getFieldLabel = (field: FormField) =>
    fieldLabelOverrides?.[field.name]
    || field.label
    || field.name.replace(/([A-Z])/g, ' $1').replace(/^./, value => value.toUpperCase());

  const getMaximumLengthMessage = (field: FormField) => {
    const fieldLabel = getFieldLabel(field);
    return `${fieldLabel} is limited to ${field.maxLength?.toLocaleString()} characters. Extra text was removed.`;
  };

  const getLegacyOverflowMessage = (field: FormField) => {
    const fieldLabel = getFieldLabel(field);
    return `${fieldLabel} exceeds ${field.maxLength?.toLocaleString()} characters and must be reduced before saving.`;
  };

  const getValidationMaximumLengthMessage = (field: FormField) =>
    `Maximum length is ${field.maxLength} characters`;

  const updateCharacterCount = (fieldName: string, value: string) => {
    setFieldCharacterCounts(previous => ({
      ...previous,
      [fieldName]: value.length
    }));
  };

  const clearTextareaLengthError = (field: FormField) => {
    setFieldErrors(previous => {
      if (![
        getMaximumLengthMessage(field),
        getLegacyOverflowMessage(field),
        getValidationMaximumLengthMessage(field)
      ].includes(previous[field.name])) {
        return previous;
      }

      const next = { ...previous };
      delete next[field.name];
      return next;
    });
  };

  const handleLimitedTextareaChange = (field: FormField, event: ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = event.currentTarget;
    const previousValue = textareaValues.current[field.name] ?? textarea.defaultValue;
    let nextValue = textarea.value;

    if (field.maxLength && previousValue.length > field.maxLength) {
      if (nextValue.length >= previousValue.length) {
        nextValue = previousValue;
        textarea.value = previousValue;
        setFieldErrors(previous => ({
          ...previous,
          [field.name]: getLegacyOverflowMessage(field)
        }));
      } else if (nextValue.length > field.maxLength) {
        setFieldErrors(previous => ({
          ...previous,
          [field.name]: getLegacyOverflowMessage(field)
        }));
      } else {
        clearTextareaLengthError(field);
      }
    } else if (field.maxLength) {
      nextValue = nextValue.slice(0, field.maxLength);

      if (textarea.value !== nextValue) {
        textarea.value = nextValue;
        setFieldErrors(previous => ({
          ...previous,
          [field.name]: getMaximumLengthMessage(field)
        }));
      } else {
        clearTextareaLengthError(field);
      }
    } else {
      clearTextareaLengthError(field);
    }

    textareaValues.current[field.name] = nextValue;
    updateCharacterCount(field.name, nextValue);
  };

  const handleLimitedTextareaPaste = (field: FormField, event: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!field.maxLength) return;

    const textarea = event.currentTarget;
    const pastedText = event.clipboardData.getData('text');
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const currentValue = textareaValues.current[field.name] ?? textarea.value;
    const pastedValue = `${currentValue.slice(0, selectionStart)}${pastedText}${currentValue.slice(selectionEnd)}`;

    if (currentValue.length > field.maxLength) {
      event.preventDefault();

      if (pastedValue.length >= currentValue.length) {
        setFieldErrors(previous => ({
          ...previous,
          [field.name]: getLegacyOverflowMessage(field)
        }));
        return;
      }

      textarea.value = pastedValue;
      textareaValues.current[field.name] = pastedValue;
      updateCharacterCount(field.name, pastedValue);
      if (pastedValue.length > field.maxLength) {
        setFieldErrors(previous => ({
          ...previous,
          [field.name]: getLegacyOverflowMessage(field)
        }));
      } else {
        clearTextareaLengthError(field);
      }
      return;
    }

    const availableCharacters = field.maxLength - (textarea.value.length - (selectionEnd - selectionStart));

    if (pastedText.length <= availableCharacters) return;

    event.preventDefault();
    const acceptedText = pastedText.slice(0, Math.max(availableCharacters, 0));
    const nextValue = `${textarea.value.slice(0, selectionStart)}${acceptedText}${textarea.value.slice(selectionEnd)}`;
    const nextCaretPosition = selectionStart + acceptedText.length;

    textarea.value = nextValue;
    textarea.setSelectionRange(nextCaretPosition, nextCaretPosition);
    textareaValues.current[field.name] = nextValue;
    updateCharacterCount(field.name, nextValue);
    setFieldErrors(previous => ({
      ...previous,
      [field.name]: getMaximumLengthMessage(field)
    }));
  };

  const parseDateFieldValue = (value: unknown): Date | null => {
    if (value === undefined || value === null || value === '') return null;

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    const stringValue = String(value).trim();
    if (!stringValue) return null;

    const dateOnlyMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    const parsedDate = new Date(stringValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const formatDateInputValue = (value: unknown): string => {
    const parsedDate = parseDateFieldValue(value);
    if (!parsedDate) return '';

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getFieldValue = (field: FormField): string => {
    const value = initialData[field.name];
    if (value === undefined || value === null) return '';

    if (field.type === 'date' && value) {
      return formatDateInputValue(value);
    }

    return String(value);
  };

  const getBooleanValue = (field: FormField): boolean => {
    const value = initialData[field.name];
    return value === true || value === 'true';
  };

  const isFieldRequired = (field: FormField): boolean => {
    const requiredValue = String(field.required).toLowerCase();
    return requiredValue === 'true' || requiredValue === 'yes';
  };

  const validateConstraint = (constraint: FormConstraint, currentValue: string, formData: FormData): string | null => {
    const compareValue = constraint.field ? formData.get(constraint.field) as string : constraint.value?.toString();
    const now = new Date();

    switch (constraint.operator) {
      case 'afterField': {
        if (!currentValue || !compareValue) return null;
        const current = new Date(currentValue);
        const compare = new Date(compareValue);
        return current <= compare ? constraint.message : null;
      }
      case 'beforeField': {
        if (!currentValue || !compareValue) return null;
        const current = new Date(currentValue);
        const compare = new Date(compareValue);
        return current >= compare ? constraint.message : null;
      }
      case 'afterNow': {
        if (!currentValue) return null;
        const current = new Date(currentValue);
        return current <= now ? constraint.message : null;
      }
      case 'beforeNow': {
        if (!currentValue) return null;
        const current = new Date(currentValue);
        return current >= now ? constraint.message : null;
      }
      case 'equalsField': {
        if (!currentValue || !compareValue) return null;
        return currentValue !== compareValue ? constraint.message : null;
      }
      case 'notFuture': {
        if (!currentValue) return null;
        const current = new Date(currentValue);
        return current > now ? constraint.message : null;
      }
      case 'notPast': {
        if (!currentValue) return null;
        const current = new Date(currentValue);
        return current < now ? constraint.message : null;
      }
      case 'requiredIf': {
        if (!compareValue || compareValue !== constraint.value?.toString()) return null;
        return !currentValue || currentValue.trim() === '' ? constraint.message : null;
      }
      default:
        return null;
    }
  };

  const validateForm = (formData: FormData): boolean => {
    const errors: Record<string, string> = {};

    fields.forEach(field => {
      const value = formData.get(field.name) as string;

      // Basic validation
      if (isFieldRequired(field) && (!value || value.trim() === '')) {
        errors[field.name] = 'This field is required';
      } else if (value && value.trim() !== '') {
        // Email format validation
        if (field.type === 'email') {
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          if (!emailRegex.test(value)) {
            errors[field.name] = 'Please enter a valid email address';
          }
        }
        // Length validations
        if (field.minLength && value.length < field.minLength) {
          errors[field.name] = `Minimum length is ${field.minLength} characters`;
        } else if (field.maxLength && value.length > field.maxLength) {
          errors[field.name] = getValidationMaximumLengthMessage(field);
        }
      } else if (field.type === 'number') {
        const numValue = Number(value);
        if (field.min !== undefined && numValue < field.min) {
          errors[field.name] = `Minimum value is ${field.min}`;
        }
        if (field.max !== undefined && numValue > field.max) {
          errors[field.name] = `Maximum value is ${field.max}`;
        }
      }

      // Constraint validation
      if (field.constraints && value) {
        for (const constraint of field.constraints) {
          const error = validateConstraint(constraint, value, formData);
          if (error) {
            errors[field.name] = error;
            break;
          }
        }
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const formData = new FormData(e.target as HTMLFormElement);

    if (!validateForm(formData)) {
      return;
    }

    setLoading(true);

    const formattedData = Object.fromEntries(
      fields.map(field => [
        field.name,
        field.type === 'date'
          ? (formData.get(field.name) as string || '').trim()
            ? (formData.get(field.name) as string).trim()
            : null
          : field.type === 'number'
            ? Number(formData.get(field.name))
            : field.type === 'boolean'
              ? formData.get(field.name) === 'on'
              : formData.get(field.name)
      ])
    );
    try {
      await onSubmit(formattedData);
      showNotification('Form submitted successfully', 'success');
    } catch (err) {
      setError('Failed to submit form: ' + (err as Error).message);
      console.error('Error submitting form:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field: FormField) => {
    const commonClasses = `w-full border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} ${THEME_CLASSES.input.placeholder} rounded-md px-3 py-1 ${fieldTextSize === 'sm' ? 'text-sm' : 'text-xs'} ${fieldErrors[field.name] ? 'border-red-500' : ''
      }`;
    const inputId = `${idPrefix}-${field.name}`;

    if (field.type === 'boolean') {
      const boolValue = getBooleanValue(field);
      return (
        <input
          id={inputId}
          type="checkbox"
          name={field.name}
          defaultChecked={boolValue}
          className={`h-4 w-4 rounded border-gray-300 ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} disabled:opacity-50`}
          disabled={disabled}
        />
      );
    }

    if (field.type === 'textarea') {
      const initialValue = getFieldValue(field);
      if (!(field.name in textareaValues.current)) {
        textareaValues.current[field.name] = initialValue;
      }
      const characterCount = fieldCharacterCounts[field.name]
        ?? initialValue.length;
      const remainingCharacters = field.maxLength
        ? Math.max(field.maxLength - characterCount, 0)
        : null;
      const helperId = field.maxLength ? `${inputId}-character-count` : undefined;
      const errorId = fieldErrors[field.name] ? `${inputId}-error` : undefined;

      return (
        <>
          <textarea
            id={inputId}
            name={field.name}
            defaultValue={initialValue}
            className={commonClasses}
            required={isFieldRequired(field)}
            placeholder={field.placeholder}
            rows={4}
            minLength={field.minLength}
            maxLength={field.maxLength && characterCount <= field.maxLength ? field.maxLength : undefined}
            disabled={disabled}
            aria-describedby={[helperId, errorId].filter(Boolean).join(' ') || undefined}
            onChange={(event) => handleLimitedTextareaChange(field, event)}
            onPaste={(event) => handleLimitedTextareaPaste(field, event)}
          />
          {remainingCharacters !== null && (
            <p
              id={helperId}
              className={`text-right text-xs ${THEME_CLASSES.text.secondary}`}
              aria-live="polite"
            >
              {remainingCharacters.toLocaleString()} characters remaining
            </p>
          )}
        </>
      );
    }

    if (field.type === 'select') {
      const options = field.optionName ? OPTIONS[field.optionName as keyof typeof OPTIONS] : field.options;

      return (
        <select
          id={inputId}
          name={field.name}
          defaultValue={getFieldValue(field)}
          className={commonClasses}
          required={isFieldRequired(field)}
          disabled={disabled}
        >
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        id={inputId}
        name={field.name}
        type={field.type}
        defaultValue={getFieldValue(field)}
        className={commonClasses}
        required={isFieldRequired(field)}
        placeholder={field.placeholder}
        min={field.min}
        max={field.max}
        minLength={field.minLength}
        maxLength={field.maxLength}
        disabled={disabled}
      />
    );
  };

  const renderFieldBlock = (field: FormField) => (
    <div key={field.name} className="space-y-1">
      <label htmlFor={`${idPrefix}-${field.name}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {getFieldLabel(field)}
        {isFieldRequired(field) && <span className="text-red-500 ml-1">*</span>}
      </label>
      {renderField(field)}
      {fieldErrors[field.name] && (
        <p id={`${idPrefix}-${field.name}-error`} className="text-red-500 text-xs mt-1" role="alert">
          {fieldErrors[field.name]}
        </p>
      )}
    </div>
  );

  const groupedFields = (() => {
    if (!fieldGroups?.length) return null;

    const assignedNames = new Set<string>();
    const groups = fieldGroups.map(group => {
      const groupFields = group.fieldNames.flatMap(fieldName => {
        if (assignedNames.has(fieldName)) return [];
        const field = fields.find(candidate => candidate.name === fieldName);
        if (!field) return [];
        assignedNames.add(fieldName);
        return [field];
      });

      return { label: group.label, fields: groupFields };
    }).filter(group => group.fields.length > 0);

    const unmatchedFields = fields.filter(field => !assignedNames.has(field.name));
    if (unmatchedFields.length > 0) {
      groups.push({ label: ungroupedFieldGroupLabel, fields: unmatchedFields });
    }

    return groups;
  })();

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col h-full">
      <div
        data-dynamic-form-grid="true"
        className={`${groupedFields ? 'space-y-5' : 'grid grid-cols-1 md:grid-cols-2'} p-4 ${compactRowSpacing ? 'gap-x-4 gap-y-2' : 'gap-4'} ${constrainFieldGrid ? 'max-h-[calc(100vh-300px)] overflow-y-auto' : ''}`}
      >
        {groupedFields
          ? groupedFields.map(group => (
            <section key={group.label} data-dynamic-form-group={group.label} className="space-y-3">
              <h4 className={`border-b pb-2 text-sm font-semibold ${THEME_CLASSES.text.primary} ${THEME_CLASSES.card.border}`}>
                {group.label}
              </h4>
              <div className={`grid grid-cols-1 md:grid-cols-2 ${compactRowSpacing ? 'gap-x-4 gap-y-2' : 'gap-4'}`}>
                {group.fields.map(renderFieldBlock)}
              </div>
            </section>
          ))
          : fields.map(renderFieldBlock)}
      </div>

      <div className="flex justify-between items-center mt-4 sticky bottom-0 p-4 border-t dark:border-gray-700 bg-white dark:bg-gray-800">
        <div>
          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}
        </div>
        <div className="flex space-x-2">
          {onCancel && (
            <button
              type="button"
              onClick={() => onCancel?.(false)}
              className={`px-4 py-2 text-sm font-medium rounded-md ${THEME_CLASSES.button.secondary}`}
            >
              {cancelLabel}
            </button>
          )}
          {!disabled && (
            <button
              type="submit"
              className={`px-4 py-2 text-sm font-medium rounded-md ${THEME_CLASSES.button.primary}`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : submitLabel}
            </button>
          )}
          {canDelete && !disabled && (
            <button
              type="button"
              onClick={onDelete}
              className={`p-2 text-sm font-medium rounded-md ${THEME_CLASSES.button.secondary}`}
              aria-label="Delete"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default DynamicForm;
